import intl from "react-intl-universal";

import {format} from 'date-fns/format'
import {formatDistance} from 'date-fns/formatDistance'

import {enUS, ru, zhCN, uk, de, hi, hy, ko, ja, fr, es, Locale} from 'date-fns/locale';

import {isToday} from 'date-fns/isToday'

export const FULL_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

const localeMap: Record<string, Locale> = {
    'en-US': enUS,
    'ru-RU': ru,
    'zh-CN': zhCN,
    'uk-UA': uk,
    'de-DE': de,
    'hi-IN': hi,
    'hy-AM': hy,
    'ko-KR': ko,
    'ja-JP': ja,
    'fr-FR': fr,
    'es-ES': es,
};

// Helper to get the correct date-fns locale
const getDateFnsLocale = (currentLocale: string) => {
    return localeMap[currentLocale] || enUS;
};

export const DAY_FORMAT = (date: Date) => {
    return format(date, 'dd/MM/yyyy');
};

export const formatTimeOnly = (date: Date) => {
    return format(date, 'h:m aa');
};

export const formatDayOnly = (date: Date) => {
    return format(date, 'dd MMM yyyy');
};

export const formatDayWithShortMonth = (date: string | Date) => {
    return format(typeof date === 'string' ? new Date(date) : date, 'MMM dd, yyyy');
};

export const formatDateShort = (date: Date) => {
    return format(date, 'MMM dd');
};

export const formatDayWithMonthAndTime = (date: string | Date) => {
    return format(typeof date === 'string' ? new Date(date) : date, 'MMM dd \'at\' h:mm a');
};

export const formatDate = (currentLocale: string, date: Date) => {
    const locale = getDateFnsLocale(currentLocale);
    if (isToday(date)) {
        return {
            date: formatDistance(date, new Date(), {addSuffix: true, locale}),
            isToday: true,
        };
    } else {
        return {
            date: formatDayOnly(date),
            isToday: false,
        };
    }
};

export const formatDateMonthAsWord = (currentLocale: string, date: Date) =>
    new Intl.DateTimeFormat(currentLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);

export function getDaysPassed(startDate: string | Date, endDate: Date = new Date()): number {
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    const diffTime = endDate.getTime() - start.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

export const getShortMonthName = (date: Date) => {
    return new Intl.DateTimeFormat('default', {month: 'short'}).format(date).slice(0, 3);
};

export const getShortWeekDayName = (date: Date) => {
    return new Intl.DateTimeFormat('default', {weekday: 'short'}).format(date).slice(0, 2);
};

export const WEEK_DAYS_SHORT = [
    intl.get("weekday.sunday.short").d("Su"),
    intl.get("weekday.monday.short").d("Mo"),
    intl.get("weekday.tuesday.short").d("Tu"),
    intl.get("weekday.wednesday.short").d("We"),
    intl.get("weekday.thursday.short").d("Th"),
    intl.get("weekday.friday.short").d("Fr"),
    intl.get("weekday.saturday.short").d("Sa"),
];

export const getFirstDayOfWeek = (): number => {
    try {
        // @ts-ignore - weekInfo is a stage 3 proposal but available in modern browsers
        const info = new Intl.Locale(getRootStore().currentLocale).weekInfo;
        return info?.firstDay ? info.firstDay % 7 : 0; // convert 7 -> 0 for Sunday
    } catch {
        return 0;
    }
};

export const getWeekDayOrder = (): number[] => {
    const firstDay = getFirstDayOfWeek();
    return Array.from({length: 7}, (_, i) => (i + firstDay) % 7);
};

export const getWeekDaysShortOrdered = (): string[] => {
    const order = getWeekDayOrder();
    return order.map(i => WEEK_DAYS_SHORT[i]);
};

export const MONTHS = [
    intl.get("month.january").d("January"),
    intl.get("month.february").d("February"),
    intl.get("month.march").d("March"),
    intl.get("month.april").d("April"),
    intl.get("month.may").d("May"),
    intl.get("month.june").d("June"),
    intl.get("month.july").d("July"),
    intl.get("month.august").d("August"),
    intl.get("month.september").d("September"),
    intl.get("month.october").d("October"),
    intl.get("month.november").d("November"),
    intl.get("month.december").d("December"),
];
